// Markdown task boxes plus the ballot-box glyphs AI drafts sometimes write instead (☐ □ unchecked; ☑ ☒ ✓ ✔ ✅ checked).
// VS16 is the emoji variation selector that may follow a glyph ("✅" + VS16 is the common emoji form).
const VS16 = String.raw`\u{FE0F}`;
const LIST_PREFIX = String.raw`(?:[-*+]\s+|\d+[.)]\s+)?`;
const MARKER = String.raw`(?:\[[ xX]\]|[☐□☑☒✓✔✅]${VS16}?)`;
const TASK_LINE = new RegExp(String.raw`^${LIST_PREFIX}(${MARKER})\s*(.*)$`, 'u');
// A drafted line can carry both kinds, e.g. "- [ ] □ Print cards"; the first marker decides completion.
const EXTRA_MARKERS = new RegExp(String.raw`^(?:${MARKER}\s*)+`, 'u');
const CHECKED_MARKER = /^\[[xX]\]|^[☑☒✓✔✅]/u;
// Leading indent and list prefix, then the run of markers with the spacing that follows it.
const MARKER_RUN = new RegExp(String.raw`^(\s*${LIST_PREFIX})((?:${MARKER}[ \t]*)+)`, 'u');

export interface PreparationChecklistItem {
    lineIndex: number;
    checked: boolean;
    text: string;
    // Unmarked "Materials:" or "## Materials" lines label a group and get no checkbox.
    heading: boolean;
}

export function preparationChecklistItems(body: string | null | undefined): PreparationChecklistItem[] {
    return (body ?? '').split(/\r?\n/).flatMap((line, lineIndex): PreparationChecklistItem[] => {
        const trimmed = line.trim();
        if (!trimmed) return [];
        const task = trimmed.match(TASK_LINE);
        if (task) {
            const checked = CHECKED_MARKER.test(task[1]);
            return [{ lineIndex, checked, text: task[2].replace(EXTRA_MARKERS, ''), heading: false }];
        }
        const markdownHeading = trimmed.match(/^#{1,6}\s+(.*)$/);
        if (markdownHeading) return [{ lineIndex, checked: false, text: markdownHeading[1], heading: true }];
        const text = trimmed.replace(/^(?:[-*+]\s+|\d+[.)]\s+)/, '');
        return [{ lineIndex, checked: false, text, heading: /[:：]$/.test(text) }];
    });
}

export function togglePreparationItem(body: string, lineIndex: number, checked: boolean): string {
    const lines = body.split(/\r?\n/);
    const item = preparationChecklistItems(body).find(row => row.lineIndex === lineIndex);
    if (!item || item.heading) return body;
    lines[lineIndex] = `- [${checked ? 'x' : ' '}] ${item.text}`;
    return lines.join(body.includes('\r\n') ? '\r\n' : '\n');
}

// A line whose marker run holds any checked marker (e.g. "- [x] ☑ Open audio") collapses to one "[ ]",
// so no checked symbol survives in the saved text; other lines and the line endings are left untouched.
export function resetPreparationChecklist(body: string): string {
    return body.split(/(\r?\n)/).map(part => part.replace(MARKER_RUN, (run, prefix: string, markers: string) => {
        const hasChecked = markers.match(new RegExp(MARKER, 'gu'))?.some(marker => CHECKED_MARKER.test(marker));
        return hasChecked ? `${prefix}[ ]${markers.match(/[ \t]*$/)?.[0] || ' '}` : run;
    })).join('');
}
